import { Form, useFormikContext } from 'formik';
import { ArrowUp } from 'lucide-react';

import { HelmRelease } from '@/react/kubernetes/helm/types';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { UseGitFormSection } from '@/react/kubernetes/helm/install/UseGitFormSection';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Modal } from '@@/modals';
import { Button, LoadingButton } from '@@/buttons';
import { WidgetIcon } from '@@/Widget/WidgetIcon';
import { Alert } from '@@/Alert';
import { Checkbox } from '@@/form-components/Checkbox';
import { Tooltip } from '@@/Tip/Tooltip';

import { K8sRegistryAccessNotice } from '../../components/K8sRegistryAccessNotice';

import { UpgradeHelmFormValues } from './types';
import { ManualUpgradeFormSections } from './ManualUpgradeFormSections';
import { GitUpgradeFormSections } from './GitUpgradeFormSections';

interface Props {
  release: HelmRelease;
  onDismiss: () => void;
  chartReferenceInfoMessage?: string;
  isCheckingChartReference: boolean;
  initialRegistryValue?: RegistryValue;
  chartReferenceState: {
    requiresSourceConfirmation: boolean;
    requiresChartConfirmation: boolean;
  };
  setPreviewIsValid: (isValid: boolean) => void;
  hasStackError: boolean;
  hasInitialVersions: boolean;
}

export function UpgradeHelmInnerForm({
  release,
  onDismiss,
  chartReferenceInfoMessage,
  isCheckingChartReference,
  initialRegistryValue,
  chartReferenceState,
  setPreviewIsValid,
  hasStackError,
  hasInitialVersions,
}: Props) {
  const environmentId = useEnvironmentId();
  const { values, setFieldValue, isSubmitting, submitForm, isValid, dirty } =
    useFormikContext<UpgradeHelmFormValues>();

  return (
    <Form>
      <Modal
        onDismiss={onDismiss}
        size="xl"
        className="flex h-[80vh] flex-col px-0"
        aria-label="upgrade-helm"
      >
        <Modal.Header
          title={
            <div className="inline-flex items-center gap-1 px-5">
              <WidgetIcon icon={ArrowUp} />
              <h2 className="m-0 ml-1 text-base">
                Edit/Upgrade {release.name}
              </h2>
            </div>
          }
          className="border-0 border-b border-solid border-gray-5 th-highcontrast:border-white th-dark:border-gray-7"
        >
          <div className="m-0 flex flex-col gap-2 px-5 pt-2">
            <span>Namespace &apos;{release.namespace}&apos;</span>
            <K8sRegistryAccessNotice
              namespace={release.namespace}
              environmentId={environmentId}
            />
          </div>
        </Modal.Header>
        <div className="flex-1 overflow-y-auto px-5">
          <Modal.Body>
            <div className="form-horizontal">
              {hasStackError && (
                <Alert color="error" className="mb-4">
                  Unable to load Git configuration for this release. Please add
                  the Git configuration again.
                </Alert>
              )}
              <UseGitFormSection
                value={values.useGitOps}
                onChange={(value) => setFieldValue('useGitOps', value)}
              />
              {values.useGitOps ? (
                <GitUpgradeFormSections
                  setPreviewIsValid={setPreviewIsValid}
                  release={release}
                />
              ) : (
                <ManualUpgradeFormSections
                  release={release}
                  chartReferenceInfoMessage={chartReferenceInfoMessage}
                  isCheckingChartReference={isCheckingChartReference}
                  initialRegistryValue={initialRegistryValue}
                  chartReferenceState={chartReferenceState}
                  setPreviewIsValid={setPreviewIsValid}
                  hasInitialVersions={hasInitialVersions}
                />
              )}
            </div>
          </Modal.Body>
        </div>
        <div className="border-0 border-t border-solid border-gray-5 px-5 th-highcontrast:border-white th-dark:border-gray-7">
          <Modal.Footer>
            <div className="flex flex-1 justify-between">
              <Button
                onClick={onDismiss}
                color="secondary"
                key="cancel-button"
                size="medium"
                data-cy="cancel-button-cy"
              >
                Cancel
              </Button>
              <div className="flex flex-1 items-center justify-end gap-4">
                {values.useGitOps && (
                  <span className="flex items-center">
                    <Checkbox
                      id="redeploy-now-checkbox"
                      label="Upgrade now"
                      checked={values.redeployNow || false}
                      onChange={(e) =>
                        setFieldValue('redeployNow', e.target.checked)
                      }
                      data-cy="redeploy-now-checkbox"
                    />
                    <Tooltip message="Upgrade the release now, instead of waiting for the next poll/webhook to trigger the upgrade." />
                  </span>
                )}
                <LoadingButton
                  type="submit"
                  color="primary"
                  key="update-button"
                  size="medium"
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid || !dirty}
                  data-cy="update-button-cy"
                  loadingText="Upgrading Helm release..."
                  isLoading={isSubmitting}
                >
                  {values.useGitOps ? 'Edit configuration' : 'Upgrade'}
                </LoadingButton>
              </div>
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </Form>
  );
}
